import {
  Button,
  Icon,
  Input,
  Modal,
  PageHeader,
  Restricted,
  Select,
  Table,
  TableColumn,
} from '@components';
import { useModal } from '@hooks';
import { useConfirmationModal } from '@hooks/useConfirmationModal';
import CollaboratorAPICaller, {
  CollaboratorFilters,
} from '@services/api/collaborator';
import CostCenterAPICaller from '@services/api/costCenter';
import { Collaborator } from 'interfaces/Collaborator';
import { CostCenter } from 'interfaces/CostCenter';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usePermissionContext } from '@contexts/PermissionProvider';

export default function CollaboratorPage() {
  const [tableData, setTableData] = useState<{
    total: number;
    result: Array<Collaborator>;
  }>({
    total: 0,
    result: [],
  });
  const [filters, setFilters] = useState<CollaboratorFilters>({});
  const [isLoading, setIsLoading] = useState(false);

  const { hasPermission } = usePermissionContext();
  const navigate = useNavigate();

  const [costCenters, setCostCenters] = useState<Array<CostCenter>>([]);
  const { isOpen, toggle: toggleModal } = useModal();
  const { isOpen: isTerminateOpen, toggle: toggleTerminateModal } = useModal();
  const [terminatingId, setTerminatingId] = useState<string | null>(null);
  const [terminationDate, setTerminationDate] = useState('');
  const confirmation = useConfirmationModal();
  const {
    watch,
    register,
    setError,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const loadCostCenters = useCallback(() => {
    if (hasPermission('costCenters', 'read')) {
      CostCenterAPICaller.listAll().then((values) => {
        setCostCenters(
          values.result.map((costCenter: CostCenter) => ({
            label: costCenter.name,
            value: costCenter.id,
          }))
        );
      });
    }
  }, [hasPermission]);

  useEffect(() => {
    loadCostCenters();
  }, [loadCostCenters]);

  const loadTableData = useCallback((currentFilters: CollaboratorFilters) => {
    setIsLoading(true);
    CollaboratorAPICaller.list(currentFilters)
      .then((values) => setTableData(values))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    loadTableData(filters);
  }, [filters, loadTableData]);

  const resetForm = () => {
    setValue('id', '');
    setValue('name', '');
    setValue('email', '');
    setValue('sherclockUrl', '');
    setValue('costCenter', undefined);
    setValue('costCenterId', '');
    setValue('startDate', '');
    setValue('terminationDate', '');
    setValue('baseCompensation', '');
    setValue('baseHealthPlan', '');
  };

  const openEditModal = async (editId: string) => {
    const collaborator = await CollaboratorAPICaller.fetch(editId);
    setValue('id', collaborator.id);
    setValue('name', collaborator.name);
    setValue('email', collaborator.email);
    setValue('sherclockUrl', collaborator.sherclockUrl);
    if (collaborator.costCenter) {
      setValue('costCenter', {
        label: collaborator.costCenter.name,
        value: collaborator.costCenter.id,
      });
    }
    setValue('costCenterId', collaborator.costCenterId);
    setValue('startDate', collaborator.startDate ?? '');
    setValue('terminationDate', collaborator.terminationDate ?? '');
    toggleModal();
  };

  const openTerminateModal = (id: string) => {
    setTerminatingId(id);
    setTerminationDate('');
    toggleTerminateModal();
  };

  const handleTerminate = async () => {
    if (!terminatingId || !terminationDate) return;
    const collaborator = tableData.result.find((c) => c.id === terminatingId);
    toggleTerminateModal();
    confirmation({
      title: 'Confirmar desligamento',
      confirmButtonTitle: 'Desligar',
      description: `Esta ação é irreversível. Desligar "${collaborator?.name}"?`,
      onSubmit: () =>
        CollaboratorAPICaller.terminate(terminatingId, { terminationDate })
          .then(() => {
            loadTableData(filters);
            toast.info(
              <div>
                <p style={{ marginBottom: '0.5rem' }}>
                  Colaborador desligado. Preencha manualmente a remuneração do
                  mês do corte na grade de estimativa.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    const q = collaborator?.name
                      ? `?q=${encodeURIComponent(collaborator.name)}`
                      : '';
                    navigate(`/estimativa-colaboradores${q}`);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-action-default)',
                    cursor: 'pointer',
                    fontWeight: 600,
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  Ir para estimativa de folha
                </button>
              </div>,
              { autoClose: false }
            );
          })
          .catch((error) => {
            const message =
              error?.response?.data?.message || 'Erro ao desligar.';
            toast.error(message);
          }),
    });
  };

  const handleDelete = async (id: string) => {
    confirmation({
      title: 'Excluir colaborador',
      confirmButtonTitle: 'Excluir',
      description: 'Deseja realmente excluir esse colaborador?',
      onSubmit: () =>
        CollaboratorAPICaller.remove(id)
          .then(() => loadTableData(filters))
          .catch((error) => {
            const message = error.response?.data?.message || 'Erro ao excluir!';
            toast.error(message);
          }),
    });
  };

  const handleCreateOrUpdate = async (data: Collaborator) => {
    const baseCompensation: number = watch('baseCompensation') || 0;
    const baseHealthPlan: number = watch('baseHealthPlan') || 0;
    const toCents = (val: number) => Math.round(val * 100);

    const payload = data.id
      ? data
      : {
          ...data,
          baseCompensationCents: toCents(baseCompensation),
          baseHealthPlanCents: toCents(baseHealthPlan),
        };

    CollaboratorAPICaller.createOrUpdate(payload, setError).then((res: any) => {
      if (res?.data?.errors) return;
      toggleModal();
      loadTableData(filters);
    });
  };

  return (
    <>
      <div className="p-s-300">
        <PageHeader title="Colaboradores" />

        <div className="mt-s-200">
          <Table
            additionalFilters={
              <div className="d-flex justify-end">
                <Restricted context="collaborators" resource="create">
                  <Button
                    className="ml-s-100"
                    suffixes={<Icon>add</Icon>}
                    design="filled"
                    onClick={() => {
                      resetForm();
                      return toggleModal();
                    }}
                  >
                    Novo colaborador
                  </Button>
                </Restricted>
              </div>
            }
            data={tableData.result}
            totalRecords={tableData.total}
            showColumnSelector={false}
            withPagination
            withSearchInput
            isLoading={isLoading}
            filters={filters}
            setFilters={setFilters}
          >
            <TableColumn fromKey="name" header="Nome" />
            <TableColumn fromKey="email" header="E-mail" />
            <TableColumn
              fromKey="costCenter.name"
              header="Centro de custo"
              render={(costCenter) => costCenter}
            />
            <TableColumn
              fromKey="status"
              header="Status"
              render={(status) => (status === 'inactive' ? 'Inativo' : 'Ativo')}
            />
            <TableColumn
              fromKey="sherclockUrl"
              header="Sherclock"
              disableSorting
              render={(url) =>
                url ? (
                  <a href={url} target="_blank" rel="noreferrer">
                    <Icon>open_in_new</Icon>
                  </a>
                ) : null
              }
            />
            <TableColumn
              width="5%"
              fromKey="id"
              header="Editar"
              disableSorting
              render={(id) => (
                <Restricted context="collaborators" resource="update">
                  <Button
                    design="transparent"
                    prefixes={<Icon>edit</Icon>}
                    onClick={() => openEditModal(id)}
                  />
                </Restricted>
              )}
            />
            <TableColumn
              width="5%"
              fromKey="id"
              header="Desligar"
              disableSorting
              render={(id) => {
                const collaborator = tableData.result.find((c) => c.id === id);
                if (collaborator?.status !== 'active') return null;
                return (
                  <Restricted context="collaborators" resource="update">
                    <Button
                      design="transparent"
                      prefixes={<Icon>person_off</Icon>}
                      onClick={() => openTerminateModal(id)}
                    />
                  </Restricted>
                );
              }}
            />
            <TableColumn
              width="5%"
              fromKey="id"
              header="Deletar"
              disableSorting
              render={(id) => (
                <Restricted context="collaborators" resource="delete">
                  <Button
                    design="transparent"
                    prefixes={<Icon>delete</Icon>}
                    onClick={async () => handleDelete(id)}
                  />
                </Restricted>
              )}
            />
          </Table>
        </div>
      </div>
      <Modal
        isOpen={isOpen}
        onClickAway={toggleModal}
        size="small"
        title="Cadastrar colaborador"
      >
        <form
          onSubmit={handleSubmit((data) =>
            handleCreateOrUpdate(data as Collaborator)
          )}
        >
          <div className="mb-s-200">
            <Input
              label="Nome"
              error={!!errors.name}
              caption={errors.name?.message as string}
              placeholder="Digite o nome"
              form={register('name', { required: 'Obrigatório' })}
            />
          </div>
          <div className="mb-s-200">
            <Input
              label="E-mail"
              type="email"
              error={!!errors.email}
              caption={errors.email?.message as string}
              placeholder="Digite o e-mail"
              form={register('email', { required: 'Obrigatório' })}
            />
          </div>
          <div className="mb-s-200">
            <Input
              label="Link do Sherclock"
              placeholder="Cole o link do Sherclock"
              form={register('sherclockUrl')}
            />
          </div>
          <div className="mb-s-200">
            <Select
              label="Centro de custo"
              value={watch('costCenter')}
              error={!!errors.costCenterId}
              caption={errors.costCenterId?.message as string}
              placeholder="Selecione um centro de custo"
              form={register('costCenterId', { required: 'Obrigatório' })}
              onSelect={(costCenter: any) => {
                setValue('costCenterId', costCenter.value);
                setValue('costCenter', costCenter);
              }}
              options={costCenters}
            />
          </div>
          <div className="mb-s-200">
            <Input
              label="Admissão"
              type="date"
              error={!!errors.startDate}
              caption={errors.startDate?.message as string}
              form={register('startDate', { required: 'Obrigatório' })}
            />
          </div>
          {!watch('id') && (
            <div className="row gap-x-2 mb-s-400">
              <div className="col-sm-6">
                <div className="form-input__container">
                  <label className="form-input__label">Remuneração base</label>
                  <div className="form-input__addons__container">
                    <NumericFormat
                      className="form-input"
                      prefix="R$ "
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={2}
                      fixedDecimalScale
                      placeholder="R$ 0,00"
                      onValueChange={({ floatValue }) =>
                        setValue('baseCompensation', floatValue ?? 0)
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-input__container">
                  <label className="form-input__label">
                    Plano de saúde base
                  </label>
                  <div className="form-input__addons__container">
                    <NumericFormat
                      className="form-input"
                      prefix="R$ "
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={2}
                      fixedDecimalScale
                      placeholder="R$ 0,00"
                      onValueChange={({ floatValue }) =>
                        setValue('baseHealthPlan', floatValue ?? 0)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          <Button className="!w-100" type="submit" alignText="center">
            Salvar
          </Button>
        </form>
      </Modal>
      <Modal
        isOpen={isTerminateOpen}
        onClickAway={toggleTerminateModal}
        size="small"
        title="Desligar colaborador"
      >
        <div className="mb-s-200">
          <div className="form-input__container">
            <label className="form-input__label">Data de desligamento</label>
            <div className="form-input__addons__container">
              <input
                className="form-input"
                type="date"
                value={terminationDate}
                onChange={(e) => setTerminationDate(e.target.value)}
              />
            </div>
          </div>
        </div>
        <p
          className="mb-s-200"
          style={{ fontSize: '0.875rem', color: 'var(--color-neutral-500)' }}
        >
          Os meses seguintes ao desligamento serão zerados. O mês do corte
          permanece editável na grade de estimativa.
        </p>
        <Button
          className="!w-100"
          alignText="center"
          disabled={!terminationDate}
          onClick={handleTerminate}
        >
          Confirmar desligamento
        </Button>
      </Modal>
    </>
  );
}
